/**
 * AcquizGroup Secure Connect - Translations
 */

export const MEETING_TRANSLATIONS = {
  en: {
    // Page titles
    meetingRoom: 'Secure Meeting Room',
    meetingHistory: 'Meeting History',
    transcriptions: 'Transcriptions',
    
    // Status
    scheduled: 'Scheduled',
    active: 'Active',
    ended: 'Ended',
    cancelled: 'Cancelled',
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
    
    // Actions
    createMeeting: 'New Meeting',
    startMeeting: 'Start Meeting',
    joinMeeting: 'Join Meeting',
    endMeeting: 'End Meeting',
    leaveMeeting: 'Leave Meeting',
    scheduleMeeting: 'Schedule Meeting',
    viewTranscription: 'View Transcription',
    downloadTranscription: 'Download Transcription',
    generateSummary: 'Generate Summary',
    
    // Form
    scheduledAt: 'Schedule Date/Time',
    recordingEnabled: 'Enable Recording',
    meetingCreated: 'Meeting created successfully',
    errorCreatingMeeting: 'Error creating meeting',
    
    // Consent
    recordingConsent: 'Recording Consent',
    consentMessage: 'This session is being recorded and transcribed for closing documentation purposes. By joining, you consent to the recording.',
    consentAccept: 'I Accept',
    consentDecline: 'Decline',
    
    // Meeting info
    deal: 'Deal',
    participants: 'Participants',
    duration: 'Duration',
    startTime: 'Start Time',
    endTime: 'End Time',
    scheduledFor: 'Scheduled For',
    createdBy: 'Created By',
    
    // Transcription
    transcription: 'Transcription',
    summary: 'Summary',
    decisions: 'Decisions Made',
    actionItems: 'Action Items',
    keyTopics: 'Key Topics',
    alerts: 'Alerts',
    searchTranscription: 'Search transcription...',
    
    // Alerts
    fraudAttempt: 'Potential Fraud Attempt',
    contactExchange: 'Contact Exchange Detected',
    offPlatform: 'Off-Platform Discussion',
    
    // Messages
    meetingStarted: 'Meeting started',
    meetingEnded: 'Meeting ended',
    participantJoined: 'joined the meeting',
    participantLeft: 'left the meeting',
    transcriptionReady: 'Transcription is ready',
    transcriptionFailed: 'Transcription failed',
    processingAudio: 'Processing audio...',
    
    // Errors
    errorStarting: 'Error starting meeting',
    errorJoining: 'Error joining meeting',
    errorEnding: 'Error ending meeting',
    errorTranscription: 'Error processing transcription',
    microphoneRequired: 'Microphone access is required',
    consentRequired: 'You must accept recording consent to join',
    
    // Form labels
    meetingTitle: 'Meeting Title',
    selectDeal: 'Select Deal',
    selectDate: 'Select Date',
    selectTime: 'Select Time',
    enableRecording: 'Enable Recording',
  },
  fr: {
    // Page titles
    meetingRoom: 'Salle de Réunion Sécurisée',
    meetingHistory: 'Historique des Réunions',
    transcriptions: 'Transcriptions',
    
    // Status
    scheduled: 'Planifiée',
    active: 'En cours',
    ended: 'Terminée',
    cancelled: 'Annulée',
    pending: 'En attente',
    processing: 'En traitement',
    completed: 'Terminée',
    failed: 'Échouée',
    
    // Actions
    createMeeting: 'Nouvelle Réunion',
    startMeeting: 'Démarrer la Réunion',
    joinMeeting: 'Rejoindre la Réunion',
    endMeeting: 'Terminer la Réunion',
    leaveMeeting: 'Quitter la Réunion',
    scheduleMeeting: 'Planifier une Réunion',
    viewTranscription: 'Voir la Transcription',
    downloadTranscription: 'Télécharger la Transcription',
    generateSummary: 'Générer le Résumé',
    
    // Form
    scheduledAt: 'Date/Heure Planifiée',
    recordingEnabled: 'Activer l\'Enregistrement',
    meetingCreated: 'Réunion créée avec succès',
    errorCreatingMeeting: 'Erreur lors de la création de la réunion',
    
    // Consent
    recordingConsent: 'Consentement d\'Enregistrement',
    consentMessage: 'Cette session est enregistrée et transcrite pour les besoins du dossier de closing. En rejoignant, vous acceptez l\'enregistrement.',
    consentAccept: 'J\'accepte',
    consentDecline: 'Refuser',
    
    // Meeting info
    deal: 'Transaction',
    participants: 'Participants',
    duration: 'Durée',
    startTime: 'Heure de début',
    endTime: 'Heure de fin',
    scheduledFor: 'Planifiée pour',
    createdBy: 'Créée par',
    
    // Transcription
    transcription: 'Transcription',
    summary: 'Résumé',
    decisions: 'Décisions Prises',
    actionItems: 'Actions à Suivre',
    keyTopics: 'Sujets Clés',
    alerts: 'Alertes',
    searchTranscription: 'Rechercher dans la transcription...',
    
    // Alerts
    fraudAttempt: 'Tentative de Fraude Potentielle',
    contactExchange: 'Échange de Coordonnées Détecté',
    offPlatform: 'Discussion Hors Plateforme',
    
    // Messages
    meetingStarted: 'Réunion démarrée',
    meetingEnded: 'Réunion terminée',
    participantJoined: 'a rejoint la réunion',
    participantLeft: 'a quitté la réunion',
    transcriptionReady: 'La transcription est prête',
    transcriptionFailed: 'La transcription a échoué',
    processingAudio: 'Traitement audio en cours...',
    
    // Errors
    errorStarting: 'Erreur au démarrage de la réunion',
    errorJoining: 'Erreur lors de la connexion à la réunion',
    errorEnding: 'Erreur lors de la fin de la réunion',
    errorTranscription: 'Erreur lors du traitement de la transcription',
    microphoneRequired: 'L\'accès au microphone est requis',
    consentRequired: 'Vous devez accepter le consentement d\'enregistrement pour rejoindre',
    
    // Form labels
    meetingTitle: 'Titre de la Réunion',
    selectDeal: 'Sélectionner une Transaction',
    selectDate: 'Sélectionner une Date',
    selectTime: 'Sélectionner une Heure',
    enableRecording: 'Activer l\'Enregistrement',
  }
};

export type MeetingTranslations = typeof MEETING_TRANSLATIONS.en;

export const getMeetingTranslations = (lang: 'en' | 'fr'): MeetingTranslations => {
  return MEETING_TRANSLATIONS[lang];
};
